import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { ContactMap } from './ContactMap';

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const formSlot = useBlockSlot('scheduletourform.form_block', {
    defaultValue: 'multihub-tour-scheduler',
  });

  return (
    <>
      {/* 50/50 split — map on the left, scheduler on the right */}
      <div className="stf-split-section">
        <div className="stf-split-container">
          <div className="stf-split-grid">
            {/* Left: map */}
            <div className="stf-split-map">
              <ContactMap />
            </div>

            {/* Right: scheduler form */}
            <div className="stf-split-form">
              <div className="stf-form-wrap">
                <EditableToken path="scheduletourform.form_block" as="div">
                  {formSlot ? (
                    <SectionContextProvider sectionId={formSlot.sectionId}>
                      <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                    </SectionContextProvider>
                  ) : (
                    <div className="stf-form-empty-card">
                      <p className="stf-form-empty">No form block selected</p>
                    </div>
                  )}
                </EditableToken>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ── 50/50 split section ── */
        .stf-split-section {
          background: white;
          padding: 2rem 0;
        }
        @media (min-width: 768px) {
          .stf-split-section { padding: 3.5rem 0; }
        }
        .stf-split-container {
          max-width: 80rem;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        @media (min-width: 1024px) {
          .stf-split-container { padding: 0 2rem; }
        }

        .stf-split-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          align-items: stretch;
        }
        @media (min-width: 768px) {
          .stf-split-grid { grid-template-columns: 1fr 1fr; gap: 2rem; }
        }

        /* On mobile, surface the form first (primary action), map below */
        @media (max-width: 767px) {
          .stf-split-form { order: 1; }
          .stf-split-map  { order: 2; }
        }

        /* Map fills its column and matches the form's height on desktop */
        .stf-split-map {
          display: flex;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
        }
        .stf-split-map .cmap-section { width: 100%; display: flex; }
        .stf-split-map .cmap-iframe,
        .stf-split-map .cmap-placeholder {
          height: 100%;
          min-height: 420px;
        }

        .stf-split-form {
          display: flex;
        }
        .stf-form-wrap {
          width: 100%;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
        }
        /* Uppercase the block's wizard heading */
        .stf-form-wrap h3 {
          text-transform: uppercase;
        }

        .stf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .stf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
